import type { Identity } from '@/auth/claims';

/** DOM id of the injected Pendo agent script — used to guarantee single injection. */
const PENDO_SCRIPT_ID = 'pendo-agent';

/**
 * Injects the Pendo agent into the page and initialises it for the current user.
 *
 * Mirrors the integration used in `cost-calculation`: the agent loader snippet is
 * appended to <head>, then `pendo.initialize` is called with visitor/account data.
 *
 * Unlike cost-calculation (whose `/me` returns `productExperience.subscriptionId`),
 * AITRAS has no `/me` endpoint and the identity claims decoded from the token
 * carry no subscription id, so it is read from the
 * `VITE_PENDO_SUBSCRIPTION_ID` environment variable. When it is missing the
 * integration is skipped so the app keeps working (e.g. in dev / tests).
 */
export function injectPendoScript(user: Identity): void {
  // Already injected — Pendo only needs to be set up once per session.
  if (document.getElementById(PENDO_SCRIPT_ID)) return;

  const subscriptionId = import.meta.env.VITE_PENDO_SUBSCRIPTION_ID as string | undefined;
  if (!subscriptionId) {
    console.warn('Pendo: VITE_PENDO_SUBSCRIPTION_ID is not set — skipping Pendo initialization.');
    return;
  }

  try {
    const head = document.querySelector('head');
    const script = document.createElement('script');
    script.id = PENDO_SCRIPT_ID;
    script.text = `!function(e,n,t,c,i){var a,o,s,d,p;for((i=e[c]=e[c]||{})._q=i._q||[],o=0,s=(a=["initialize","identify","updateOptions","pageLoad","track"]).length;o<s;++o)!function(e){i[e]=i[e]||function(){i._q[e===a[0]?"unshift":"push"]([e].concat([].slice.call(arguments,0)))}}(a[o]);(d=n.createElement(t)).async=!0,d.src="https://content.pendo.xtm-intl.com/agent/static/${subscriptionId}/pendo.js",(p=n.getElementsByTagName(t)[0]).parentNode.insertBefore(d,p)}(window,document,"script","pendo");`;
    if (head) {
      head.appendChild(script);
    }

    pendo.initialize({
      visitor: {
        id: `${user.tenantId ?? 'no-tenant'}-${user.subject}`,
        userId: user.subject,
        email: user.email,
      },
      account: {
        id: user.tenantId ?? user.subject,
      },
    });
  } catch (error) {
    console.warn('Failed to inject Pendo script', error);
  }
}

/**
 * Initialises Pendo with pre-fetched user data. Safe to call repeatedly;
 * the underlying injection is idempotent.
 */
export function initializePendo(user: Identity): void {
  try {
    injectPendoScript(user);
  } catch (error) {
    console.warn('Failed to initialize Pendo', error);
  }
}
